import React, { useEffect, useMemo, useState } from "react";
import { StockMenu, WatchAdd } from "@/components/WatchAdd";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { Radar, Layers, TrendingUp, TrendingDown, Moon, Copy, Filter } from "lucide-react";

const TV = { FINNIFTY: "CNXFINANCE", MIDCPNIFTY: "NIFTY_MID_SELECT" };
const plainMode = () => localStorage.getItem("tfr_copy_plain") === "1";
export const tvList = (symbols) => (plainMode()
  ? symbols.join(",")
  : symbols.map((x) => `NSE:${(TV[x] || x).replace(/[&-]/g, "_")}`).join(","));

export async function copySymbols(symbols, name) {
  if (!symbols.length) return toast.error("No stocks to copy");
  const text = tvList(symbols);
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`Copied ${symbols.length} symbols${plainMode() ? "" : " — paste into a TradingView watchlist"}`);
  } catch {
    const blob = new Blob([text], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `${name}.txt`; a.click();
    toast.success(`Downloaded ${symbols.length} symbols as ${name}.txt`);
  }
}

export async function copyAll(sections) {
  const text = sections.map(([title, syms]) => `${title} (${syms.length})\n${syms.length ? tvList(syms) : "—"}`).join("\n\n");
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`Copied ${sections.length} lists`);
  } catch {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([text], { type: "text/plain" })); a.download = "tfr-all-lists.txt"; a.click();
    toast.success("Downloaded all lists as tfr-all-lists.txt");
  }
}

const DownloadBtn = ({ rows, name, testid }) => (
  <button onClick={() => copySymbols(rows.map((r) => r.symbol), name)} data-testid={testid} title="Copy symbols (comma-separated)"
    className="h-6 w-6 grid place-items-center rounded-md border border-slate-200 text-slate-500 hover:text-blue-700 hover:border-blue-300 transition-colors">
    <Copy className="h-3.5 w-3.5" />
  </button>
);

const Chg = ({ v }) => (
  <span className={`font-mono text-[12px] font-semibold ${v > 0 ? "text-emerald-600" : v < 0 ? "text-rose-600" : "text-slate-500"}`}>
    {v > 0 ? "+" : ""}{v ?? "—"}%
  </span>
);

function Row({ r, meta, testid }) {
  return (
    <div className="flex items-start gap-1">
    <StockMenu instrumentId={r.instrument_id} symbol={r.symbol}>
    <button data-testid={testid}
      className="flex-1 min-w-0 text-left rounded-lg px-3 py-2 border border-transparent hover:border-slate-200 hover:bg-slate-50 transition-colors">
      <div className="flex items-center justify-between gap-2">
        <span className="font-display font-bold text-slate-900 text-[14px]" title={r.name}>{r.symbol}</span>
        <span className="flex items-center gap-2.5 shrink-0">
          <span className="font-mono text-[13px] text-slate-900">₹{inr(r.close)}</span>
          <Chg v={r.change_pct} />
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1">
        <span className="text-[10px] font-mono text-slate-400">{r.sector}</span>
        {meta}
      </div>
    </button>
    </StockMenu>
    <WatchAdd instrumentId={r.instrument_id} symbol={r.symbol} className="mt-2.5" />
    </div>
  );
}

function StudyPanel({ n, title, hint, icon: Icon, tone, rows, empty, testid, meta, fileName }) {
  const navigate = useNavigate();
  const [all, setAll] = useState(false);
  const shown = all ? rows : rows.slice(0, 8);
  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 flex flex-col" data-testid={testid}>
      <header className="flex items-start justify-between gap-2 mb-3">
        <div>
          <h2 className="font-display font-bold text-slate-900 text-[15px] flex items-center gap-2">
            <span className={`h-6 w-6 rounded-md grid place-items-center text-[11px] font-mono text-white ${tone}`}>{n}</span>
            <Icon className="h-4 w-4 text-slate-500" /> {title}
          </h2>
          <p className="text-[11px] text-slate-500 mt-1 font-mono">{hint}</p>
        </div>
        <span className="flex items-center gap-1.5 shrink-0">
          <span className="font-mono text-xs text-slate-600 bg-slate-100 rounded-full px-2 py-0.5" data-testid={`${testid}-count`}>{rows.length}</span>
          <DownloadBtn rows={rows} name={fileName} testid={`${testid}-download`} />
        </span>
      </header>
      <div className={`space-y-0.5 ${all ? "max-h-[460px] overflow-y-auto pr-1" : ""}`}>
        {shown.map((r) => (
          <Row key={r.instrument_id} r={r} meta={meta(r)} testid={`${testid}-${r.symbol}`} />
        ))}
        {rows.length === 0 && <p className="text-slate-400 text-xs font-mono text-center py-6" data-testid={`${testid}-empty`}>{empty}</p>}
      </div>
      {rows.length > 8 && (
        <button onClick={() => setAll((v) => !v)} data-testid={`${testid}-toggle`}
          className="mt-2 text-[11px] font-mono text-blue-700 hover:text-blue-900">{all ? "Show less" : `Show all ${rows.length}`}</button>
      )}
    </section>
  );
}

const widthTag = (r) => (<>
  <BmvBadge r={r} />
  <span className="text-[10px] font-mono text-violet-700 bg-violet-100/80 px-1.5 rounded">width {r.width}%</span>
  {r.data_check && <span className="text-[10px] font-mono text-amber-800 bg-amber-100 border border-amber-300 px-1.5 rounded" data-testid={`data-check-${r.symbol}`}
    title="Official exchange close not received yet (or a session is missing). Zone is provisional and will update after the evening data refresh.">data check</span>}
</>);
const BmvBadge = ({ r }) => (r.bmv ? (
  <span data-testid={`bmv-${r.symbol}`} title={`Close came on ${r.rvol}× the 20-day average volume`}
    className={`text-[10px] font-mono font-bold px-1.5 rounded ${r.bmv >= 3 ? "text-white bg-amber-500" : "text-amber-800 bg-amber-100 border border-amber-300"}`}>
    Big Money {r.bmv >= 3 ? "3×+" : "2×"} · {r.rvol}×
  </span>) : null);
const zoneTag = (label) => (r) => (<>
  <BmvBadge r={r} />
  <span className={`text-[10px] font-mono px-1.5 rounded ${r.dir === "up" ? "text-emerald-700 bg-emerald-100/80" : "text-rose-700 bg-rose-100/80"}`}>
    {r.dir === "up" ? `closed above ${label} · ${r.tc}` : `closed below ${label} · ${r.bc}`}
  </span>
  {r.data_check && <span className="text-[10px] font-mono text-amber-800 bg-amber-100 border border-amber-300 px-1.5 rounded" data-testid={`data-check-${r.symbol}`}
    title="Official exchange close not received yet. Will update after the evening data refresh.">data check</span>}
</>);
const radarTag = (r) => (<>
  {r.tight && <span className="text-[10px] font-mono font-bold px-1.5 rounded text-white bg-sky-500" data-testid={`radar-tight-${r.symbol}`}>TIGHT</span>}
  <span className="text-[10px] font-mono text-sky-700 bg-sky-100/80 px-1.5 rounded" title={`EMA20 ${r.e20} · EMA50 ${r.e50} · EMA200 ${r.e200}`}>
    spread {r.spread}%
  </span>
  <span className={`text-[10px] font-mono px-1.5 rounded ${r.stack === "bull" ? "text-emerald-700" : r.stack === "bear" ? "text-rose-700" : "text-slate-500"}`}>
    {r.stack === "bull" ? "20>50>200" : r.stack === "bear" ? "20<50<200" : "mixed"}
  </span>
</>);
const streakTag = (unit) => (r) => (<>
  <BmvBadge r={r} />
  <span className="text-[10px] font-mono text-violet-700 bg-violet-100/80 px-1.5 rounded" title={`Flow Zone width for each ${unit}, newest first`}>
    widths {r.widths?.map((w) => `${w}%`).join(" · ")}
  </span>
  {r.data_check && <span className="text-[10px] font-mono text-amber-800 bg-amber-100 border border-amber-300 px-1.5 rounded" data-testid={`data-check-${r.symbol}`}
    title="Official exchange close not received yet. Will update after the evening data refresh.">data check</span>}
</>);

export default function Scanner() {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => api.get("/today/after-session").then((r) => setD(r.data)).catch(() => setD({ lists: {} }));
    load();
    const t = setInterval(load, 300000);
    return () => clearInterval(t);
  }, []);
  const L = d?.lists || {};
  const pending = d?.daily_pending;
  const nd = d?.narrow_pct || {};
  const nextWk = d?.week_basis === "next";
  const tomorrowEmpty = pending ? "Available after the 3:30 PM close" : "None for the next session";
  const P = [
    { n: 1, key: "d_narrow", title: "Tomorrow · Narrow Day Flow Zone", icon: Layers, tone: "bg-violet-600",
      hint: `next session's Day Flow Zone width ≤ ${nd.D ?? "—"}% · tightest first`, empty: tomorrowEmpty, meta: widthTag },
    { n: 2, key: "d_narrow_inside", title: "Tomorrow · Narrow Inside Day Flow Zone", icon: Layers, tone: "bg-amber-500",
      hint: `width ≤ ${d?.narrow_inside_pct?.D ?? "—"}% AND nested inside today's zone · coiled`, empty: tomorrowEmpty, meta: widthTag },
    { n: 3, key: "w_narrow", title: `Narrow Week Flow Zone · ${nextWk ? "next week" : "this week"}`, icon: Layers, tone: "bg-fuchsia-600",
      hint: `${nextWk ? "next week's zone (from this week's candles)" : "this week's zone (from last week's candles)"} width ≤ ${nd.W ?? "—"}% · tightest first`, empty: "None this week", meta: widthTag },
    { n: 4, key: "w_narrow_inside", title: `Narrow Inside Week Flow Zone · ${nextWk ? "next week" : "this week"}`, icon: Layers, tone: "bg-orange-500",
      hint: `width ≤ ${d?.narrow_inside_pct?.W ?? "—"}% AND nested inside ${nextWk ? "this week's" : "last week's"} zone`, empty: "None this week", meta: widthTag },
    { n: 5, key: "wz_up", title: "Daily Close Above Week Flow Zone", icon: TrendingUp, tone: "bg-emerald-600",
      hint: "last daily candle closed above the week's zone in force · fresh (prev day not above)", empty: "No fresh close above", meta: zoneTag("week zone") },
    { n: 6, key: "wz_down", title: "Daily Close Below Week Flow Zone", icon: TrendingDown, tone: "bg-rose-600",
      hint: "last daily candle closed below the week's zone in force · fresh (prev day not below)", empty: "No fresh close below", meta: zoneTag("week zone") },
    { n: 7, key: "mz_up", title: "Daily Close Above Month Flow Zone", icon: TrendingUp, tone: "bg-teal-600",
      hint: "last daily candle closed above the month's zone in force · fresh (prev day not above)", empty: "No fresh close above", meta: zoneTag("month zone") },
    { n: 8, key: "mz_down", title: "Daily Close Below Month Flow Zone", icon: TrendingDown, tone: "bg-red-700",
      hint: "last daily candle closed below the month's zone in force · fresh (prev day not below)", empty: "No fresh close below", meta: zoneTag("month zone") },
    { n: 9, key: "d_narrow2", title: "Narrow Day Flow Zone · 2 days in a row", icon: Layers, tone: "bg-indigo-600",
      hint: `the latest 2 day zones (newest = next session) each ≤ ${nd.D ?? "—"}%`, empty: "No 2-day streak", meta: streakTag("day") },
    { n: 10, key: "d_narrow3", title: "Narrow Day Flow Zone · 3 days in a row", icon: Layers, tone: "bg-violet-600",
      hint: `the latest 3 day zones each ≤ ${nd.D ?? "—"}%`, empty: "No 3-day streak", meta: streakTag("day") },
    { n: 11, key: "w_narrow2", title: "Narrow Week Flow Zone · 2 weeks in a row", icon: Layers, tone: "bg-fuchsia-600",
      hint: `the latest 2 week zones (newest = ${nextWk ? "next week" : "this week"}) each ≤ ${nd.W ?? "—"}%`, empty: "No 2-week streak", meta: streakTag("week") },
    { n: 12, key: "w_narrow3", title: "Narrow Week Flow Zone · 3 weeks in a row", icon: Layers, tone: "bg-pink-600",
      hint: `the latest 3 week zones each ≤ ${nd.W ?? "—"}%`, empty: "No 3-week streak", meta: streakTag("week") },
    { n: 13, key: "ema_radar", title: "TFR RADAR", icon: Radar, tone: "bg-sky-600",
      hint: "all F&O stocks · daily EMA 20, 50 & 200 within 1% of price (tight ≤ 0.5%)", empty: "No EMA squeeze right now", meta: radarTag },
  ];
  const [picked, setPicked] = useState([]);
  const [minN, setMinN] = useState(0);
  const [plain, setPlain] = useState(plainMode());
  const setCopyPlain = (v) => { localStorage.setItem("tfr_copy_plain", v ? "1" : "0"); setPlain(v); };
  const toggle = (n) => setPicked((v) => (v.includes(n) ? v.filter((x) => x !== n) : [...v, n].sort((a, b) => a - b)));
  const [bmvMin, setBmvMin] = useState(Number(localStorage.getItem("tfr_bmv_min") || 0));
  const pickBmv = (v) => { localStorage.setItem("tfr_bmv_min", String(v)); setBmvMin(v); };
  const [sec, setSec] = useState(null);
  const sectorCounts = useMemo(() => {
    const seen = {};
    Object.values(L).forEach((rows) => (rows || []).forEach((r) => { if (r.sector) (seen[r.sector] ||= new Set()).add(r.symbol); }));
    return Object.entries(seen).map(([s, set]) => [s, set.size]).sort((a, b) => b[1] - a[1]);
  }, [d]); // eslint-disable-line react-hooks/exhaustive-deps
  const rowsFor = (k) => (L[k] || []).filter((r) => (r.bmv || 0) >= bmvMin && (!sec || r.sector === sec));

  const common = useMemo(() => {
    const m = {};
    P.forEach((p) => rowsFor(p.key).forEach((r) => {
      m[r.symbol] = m[r.symbol] || { ...r, lists: [] };
      if (!m[r.symbol].lists.includes(p.n)) m[r.symbol].lists.push(p.n);
    }));
    return Object.values(m)
      .filter((r) => picked.every((n) => r.lists.includes(n)) && r.lists.length >= minN)
      .sort((a, b) => b.lists.length - a.lists.length || a.symbol.localeCompare(b.symbol));
  }, [d, picked, minN, bmvMin, sec]); // eslint-disable-line react-hooks/exhaustive-deps
  const filtering = picked.length > 0 || minN > 0;

  return (
    <div className="space-y-5" data-testid="scanner-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Radar className="h-6 w-6 text-blue-600" /> TFR Institutional Scanner
          </h1>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5" data-testid="scanner-subtitle">
            <Moon className="h-3.5 w-3.5" /> After the Session Study · session {d?.session_date || "…"}
          </p>
        </div>
        {pending && (
          <span className="text-xs font-mono text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-3 py-1" data-testid="scanner-pending">
            Market open · tomorrow's lists unlock after 3:30 PM
          </span>
        )}
      </div>
      {d?.stale && (
        <div className="rounded-xl border border-rose-300 bg-rose-50 px-4 py-2.5 text-sm text-rose-800" data-testid="scanner-stale-warning">
          <b>Data is from {d.session_date}, not the latest session ({d.expected_session}).</b>{" "}
          {d.live_enabled === false
            ? "You are on the PREVIEW copy, which never connects to Dhan. Open the live site to see real, current lists."
            : "Lists below are based on old data — reconnect Dhan in Admin → Data Feed to refresh."}
        </div>
      )}
      <div className="rounded-2xl border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="common-filter">
        <span className="flex items-center gap-1.5 text-xs font-bold text-slate-700"><Filter className="h-3.5 w-3.5 text-blue-600" /> Common stocks</span>
        <span className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[11px] font-mono text-slate-500">in all of:</span>
          {P.map((p) => (
            <button key={p.n} onClick={() => toggle(p.n)} data-testid={`common-pick-${p.n}`} title={p.title}
              className={`h-7 min-w-[28px] px-2 rounded-md text-[11px] font-mono font-bold border transition-colors ${picked.includes(p.n) ? `${p.tone} text-white border-transparent` : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{p.n}</button>
          ))}
        </span>
        <label className="flex items-center gap-1.5 text-[11px] font-mono text-slate-500">
          in at least
          <select value={minN} onChange={(e) => setMinN(Number(e.target.value))} data-testid="common-min-lists"
            className="h-7 rounded-md border border-slate-200 bg-white px-1.5 text-[11px] text-slate-800">
            <option value={0}>any</option>
            {[2, 3, 4, 5, 6].map((k) => <option key={k} value={k}>{k} lists</option>)}
          </select>
        </label>
        <span className="flex items-center gap-1.5 text-[11px] font-mono text-slate-500" data-testid="bmv-filter">
          Big Money
          <span className="inline-flex rounded-md border border-slate-200 overflow-hidden">
            {[[0, "All"], [2, "2×+"], [3, "3×+"]].map(([v, lbl]) => (
              <button key={v} onClick={() => pickBmv(v)} data-testid={`bmv-filter-${v}`}
                className={`px-2 h-7 text-[11px] font-mono font-bold ${bmvMin === v ? (v ? "bg-amber-500 text-white" : "bg-blue-600 text-white") : "bg-white text-slate-600 hover:bg-slate-50"}`}>{lbl}</button>
            ))}
          </span>
        </span>
        <button data-testid="copy-all-lists"
          onClick={() => copyAll([
            ...(filtering ? [["★ Common Stocks", common.map((r) => r.symbol)]] : []),
            ...P.map((p) => [`${p.n} · ${p.title}`, rowsFor(p.key).map((r) => r.symbol)]),
          ])}
          className="sm:ml-auto flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-blue-200 bg-blue-50 text-[11px] font-mono font-bold text-blue-700 hover:bg-blue-100 transition-colors">
          <Copy className="h-3.5 w-3.5" /> Copy all lists
        </button>
        <span className="flex items-center gap-1.5 text-[11px] font-mono text-slate-500" data-testid="copy-format">
          copy as
          <span className="inline-flex rounded-md border border-slate-200 overflow-hidden">
            {[[false, "NSE:INFY"], [true, "INFY"]].map(([v, lbl]) => (
              <button key={lbl} onClick={() => setCopyPlain(v)} data-testid={`copy-format-${v ? "plain" : "nse"}`}
                className={`px-2 h-7 text-[11px] font-mono ${plain === v ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{lbl}</button>
            ))}
          </span>
        </span>
        {filtering && (
          <button onClick={() => { setPicked([]); setMinN(0); }} data-testid="common-clear" className="text-[11px] font-mono text-blue-700 hover:text-blue-900">clear</button>
        )}
      </div>

      {d && sectorCounts.length > 0 && (
        <div className="flex flex-wrap items-center gap-1" data-testid="sector-chips">
          <span className="text-[10px] font-mono text-slate-500 mr-1">SECTOR</span>
          <button onClick={() => setSec(null)} data-testid="sector-chip-all"
            className={`h-6 px-2 text-[10px] font-mono font-bold border ${!sec ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-600 hover:border-blue-500"}`}>All</button>
          {sectorCounts.map(([s, n]) => (
            <button key={s} onClick={() => setSec(sec === s ? null : s)} data-testid={`sector-chip-${s.replace(/\W+/g, "-")}`}
              className={`h-6 px-2 text-[10px] font-mono border ${sec === s ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-600 hover:border-blue-500"}`}>
              {s} <span className={sec === s ? "text-blue-100" : "text-slate-400"}>{n}</span>
            </button>
          ))}
        </div>
      )}

      {d && filtering && (
        <StudyPanel n="★" title="Common Stocks" icon={Filter} tone="bg-blue-600" rows={common} testid="study-common" fileName="tfr-common"
          hint={`${picked.length ? `in lists ${picked.join(" + ")}` : "any lists"}${minN ? ` · at least ${minN} lists` : ""} · most lists first`}
          empty="No stock matches this combination"
          meta={(r) => <span className="flex gap-0.5 items-center"><BmvBadge r={r} />{r.lists.map((n) => (
            <span key={n} className={`text-[9px] font-mono font-bold text-white rounded px-1 ${P[n - 1].tone}`}>{n}</span>))}</span>} />
      )}

      {!d ? (
        <p className="text-slate-400 font-mono text-sm" data-testid="scanner-loading">Loading study…</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {P.map((p) => (
            <StudyPanel key={p.key} {...p} rows={rowsFor(p.key)} testid={`study-${p.key.replace(/_/g, "-")}`} fileName={`tfr-${p.n}-${p.key}`} />
          ))}
        </div>
      )}
    </div>
  );
}
